import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

// Bulk-tag every part applicable to a sister equipment onto this one.
// Shows a full preview (new vs already-tagged) before applying.
const CopyApplicablePartsDialog = ({ open, onClose, machineryId, machineryName, onSuccess }) => {
  const [machinery, setMachinery] = useState([]);
  const [locations, setLocations] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [serialized, setSerialized] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSourceId('');
    (async () => {
      setLoading(true);
      try {
        const [mRes, locRes, invRes, serRes] = await Promise.all([
          axios.get(`${API}/machinery`),
          axios.get(`${API}/locations`),
          axios.get(`${API}/inventory`),
          axios.get(`${API}/serialized-components`).catch(() => ({ data: [] })),
        ]);
        setMachinery(mRes.data || []);
        setLocations(locRes.data || []);
        setInventory(invRes.data || []);
        setSerialized(serRes.data || []);
      } catch {
        toast.error('Failed to load equipment list');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const locName = useMemo(() => {
    const m = new Map();
    locations.forEach((l) => m.set(l.id, l.name));
    return m;
  }, [locations]);

  const options = useMemo(
    () =>
      machinery
        .filter((m) => m.id !== machineryId)
        .map((m) => ({
          id: m.id,
          label: `${locName.get(m.plant_location_id) || 'Unassigned'} → ${m.component}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [machinery, machineryId, locName]
  );

  const preview = useMemo(() => {
    if (!sourceId) return null;
    const has = (arr, id) => (arr || []).includes(id);
    const bulkNew = inventory
      .filter((i) => has(i.applicable_machinery_ids, sourceId) && !has(i.applicable_machinery_ids, machineryId))
      .sort((a, b) => (a.part_number || '').localeCompare(b.part_number || ''));
    const bulkAlready = inventory.filter(
      (i) => has(i.applicable_machinery_ids, sourceId) && has(i.applicable_machinery_ids, machineryId)
    ).length;
    const serNew = serialized.filter(
      (s) => has(s.applicable_machinery_ids, sourceId) && !has(s.applicable_machinery_ids, machineryId)
    ).length;
    return { bulkNew, bulkAlready, serNew };
  }, [sourceId, inventory, serialized, machineryId]);

  const nothingToCopy = preview && preview.bulkNew.length === 0 && preview.serNew === 0;

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/machinery/${machineryId}/copy-applicable-parts`, {
        source_machinery_id: sourceId,
      });
      const { bulk_tagged, serialized_tagged } = res.data;
      toast.success(
        `Tagged ${bulk_tagged} part${bulk_tagged === 1 ? '' : 's'}` +
        (serialized_tagged ? ` + ${serialized_tagged} serialized` : '') +
        ` to ${machineryName}`
      );
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy parts');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="copy-applicable-parts-dialog">
        <DialogHeader>
          <DialogTitle>Copy Applicable Parts</DialogTitle>
          <DialogDescription>
            Tag every part that applies to another piece of equipment onto <span className="font-medium">{machineryName}</span>.
            Great for sister compressors — nothing is removed from the source.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="copy-parts-source">Copy from equipment</Label>
            <select
              id="copy-parts-source"
              data-testid="copy-applicable-parts-source-select"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              disabled={loading}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">{loading ? 'Loading…' : 'Select source equipment'}</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>

          {preview && (
            <div className="space-y-2" data-testid="copy-applicable-parts-preview">
              {nothingToCopy ? (
                <div className="text-sm text-slate-500 italic border rounded-md p-3">
                  Nothing new to copy — that equipment has no applicable parts that aren&apos;t already tagged here.
                </div>
              ) : (
                <>
                  <div className="text-sm font-medium">
                    {preview.bulkNew.length} part{preview.bulkNew.length === 1 ? '' : 's'} will be tagged
                    {preview.serNew > 0 && ` (+ ${preview.serNew} serialized)`}
                    {preview.bulkAlready > 0 && (
                      <span className="text-slate-500 font-normal"> · {preview.bulkAlready} already tagged (skipped)</span>
                    )}
                  </div>
                  {preview.bulkNew.length > 0 && (
                    <div className="border rounded-md divide-y max-h-56 overflow-y-auto">
                      {preview.bulkNew.map((p) => (
                        <div key={p.id} className="px-3 py-1.5 text-xs" data-testid={`copy-parts-preview-row-${p.id}`}>
                          <span className="font-mono font-semibold">{p.part_number}</span>
                          <span className="text-slate-500 ml-2">{p.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="copy-applicable-parts-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={!sourceId || applying || nothingToCopy}
              data-testid="copy-applicable-parts-apply-btn"
            >
              {applying ? 'Copying…' : 'Copy Parts'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyApplicablePartsDialog;
